import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { getSubtopicsByTopicKey } from "../../../helper/apicalls/course";
import {
  createTopicFolder,
  getFolders,
  getQuestionBanks,
  updateFolder,
  deleteFolder,
  getInstitutionQuestionBanks,
  updateInstitutionQuestionBankFolder,
  deleteInstitutionQuestionBankFolder,
  getAdminQuestionBankFoldersInInstitution,
  getAdminQuestionBanksInInstitution,
} from "../../../helper/apicalls/questionBank";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import Breadcrumbs from "../../common/Breadcrumbs";
import { HomeOutlinedIcon, AddIcon, FolderIcon } from "../../../icons";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import GlobalLoader from "../../common/GlobalLoader";
import GenerateQuestionBankPopup from "../../common/GenerateQuestionBankPopup";
import QuestionBankViewPopup from "../../common/QuestionBankViewPopup";
import "./../../../styles/admin/questionBank.scss";

const QuestionBankSubtopics = () => {
  const { courseKey, subjectKey, topicKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isInstitution =
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/teacher");
  const basePath = location.pathname.startsWith("/institution")
    ? "/institution/question_banks"
    : location.pathname.startsWith("/teacher")
    ? "/teacher/question_banks"
    : "/admin/question-bank";

  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  let permissionsData = [];
  try {
    const parsed = permissions ? JSON.parse(permissions) : [];
    permissionsData = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }
  const questionBankPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "questionbank"
  );
  const hasWritePermission =
    role === "admin" || (questionBankPermission ? !!questionBankPermission.write : false);
  const isTempTopic = topicKey?.includes("-temp-");
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inTempSubtopicView, setInTempSubtopicView] = useState(false);
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const [folders, setFolders] = useState([]);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderBanks, setFolderBanks] = useState([]);
  const [viewBankId, setViewBankId] = useState(null);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isTempTopic) {
        setSubtopics([]);
        setInTempSubtopicView(true);
        return;
      }

      const subtopicsData = await getSubtopicsByTopicKey(topicKey);

      let foldersData = [];
      try {
        if (isInstitution) {
          const adminFolders = await getAdminQuestionBankFoldersInInstitution("topic", topicKey);
          const institutionFolders = await getFolders("topic", topicKey);
          foldersData = [
            ...adminFolders.map(f => ({ ...f, is_admin: true })),
            ...institutionFolders.map(f => ({ ...f, is_admin: false }))
          ];
        } else {
          foldersData = await getFolders("topic", topicKey);
        }
      } catch (folderErr) {
        console.error(
          "Error fetching folders for Question Bank (topic level):",
          folderErr
        );
        foldersData = [];
      }

      if (Array.isArray(subtopicsData) && subtopicsData.length > 0) {
        setSubtopics(subtopicsData);
      } else {
        setSubtopics([]);
      }

      setFolders(Array.isArray(foldersData) ? foldersData : []);
    } catch (err) {
      console.error("Error fetching subtopics for Question Bank:", err);
      setError("Failed to fetch subtopics");
      setSubtopics([]);
    } finally {
      setLoading(false);
    }
  };

  const closeFolderMenu = () => {
    setFolderMenuOpenId(null);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
    closeFolderMenu();
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
    closeFolderMenu();
  };

  const handleQuestionBankClick = (qb) => {
    if (!qb || !qb.id) return;
    setViewBankId(qb.id);
    setIsViewPopupOpen(true);
  };

  const handleBackFromFolder = () => {
    setActiveFolder(null);
    setFolderBanks([]);
  };

  const handleSubtopicClick = (sub) => {
    if (sub?.key && String(sub.key).includes("-temp-")) {
      setInTempSubtopicView(true);
      return;
    }

    navigate(
      `${basePath}/course/${courseKey}/subject/${subjectKey}/topic/${topicKey}/subtopic/${sub.key}`
    );
  };

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const handleFolderClick = async (folder) => {
    try {
      setLoading(true);
      setError(null);
      setActiveFolder(folder);
      let data;
      if (folder.is_admin) {
        data = await getAdminQuestionBanksInInstitution(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: true }));
        }
      } else {
        const listQuestionBanks = isInstitution
          ? getInstitutionQuestionBanks
          : getQuestionBanks;
        data = await listQuestionBanks(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: false }));
        }
      }
      setFolderBanks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching question banks for folder:", err);
      setError("Failed to fetch question banks");
      setFolderBanks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [courseKey, subjectKey, topicKey, location]);

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            <span>Topic Question Bank</span>
            <div className="qb-card-actions">
              {!activeFolder && hasWritePermission && (
                <>
                  <button
                    type="button"
                    className="qb-add-btn"
                    onClick={handleAddFolder}
                  >
                    <AddIcon className="action-button-icon" />
                    <span>Add Folder</span>
                  </button>
                </>
              )}
              {activeFolder && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={handleBackFromFolder}
                >
                  Back
                </button>
              )}
            </div>
          </div>
          {!activeFolder && (
            <div className="folder-grid">
              {!loading &&
                !error &&
                !inTempSubtopicView &&
                subtopics?.map((sub) => {
                  const id = sub.id || sub.key;
                  return (
                    <div
                      key={id}
                      className="folder-item"
                      onClick={() => handleSubtopicClick(sub)}
                    >
                      <div className="folder-icon">
                        <FolderIcon style={{ color: "#fccc77" }} />
                      </div>
                      <div className="folder-name">{sub.name}</div>
                    </div>
                  );
                })}

              {!loading &&
                !error &&
                !inTempSubtopicView &&
                folders?.map((folder) => {
                  const id = folder.id || folder.key;
                  return (
                    <div
                      key={id}
                      className="folder-item"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <div className="folder-icon">
                        <FolderIcon style={{ color: "var(--folder-bg)" }} />
                      </div>
                      <div className="folder-name">{folder.name}</div>
                      {isInstitution && folder.is_admin && (
                        <div className="qb-default-badge">Default</div>
                      )}
                      {!folder.is_admin && hasWritePermission && (
                        <button
                          type="button"
                          className="folder-menu-button"
                          onClick={(e) => handleFolderMenuToggle(id, e)}
                        >
                          <MoreVertIcon />
                        </button>
                      )}
                      {folderMenuOpenId === id && (
                        <div
                          className="folder-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderEdit(folder, e)}
                          >
                            Edit
                          </div>
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderDelete(folder, e)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

              {!loading && !error && inTempSubtopicView && (
                <div
                  className="folder-item file-item"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <div className="folder-icon">
                    <AddIcon />
                  </div>
                  <div className="folder-name">Add Question Bank</div>
                </div>
              )}
            </div>
          )}

          {activeFolder && (
            <div className="folder-grid">
              {!loading && !error && !activeFolder.is_admin && hasWritePermission && (
                <div
                  className="folder-item file-item"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <div className="folder-icon">
                    <AddIcon />
                  </div>
                  <div className="folder-name">Add Question Bank</div>
                </div>
              )}

              {!loading &&
                !error &&
                folderBanks?.map((qb) => (
                  <div
                    key={qb.id}
                    className="folder-item file-item"
                    onClick={() => handleQuestionBankClick(qb)}
                  >
                    <div className="folder-icon">📄</div>
                    <div className="folder-name">{qb.name || qb.id}</div>
                    {isInstitution && qb.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                  </div>
                ))}

              {error && <div className="qb-error">{error}</div>}
            </div>
          )}
        </div>
      </div>

      {showGeneratePopup && <GenerateQuestionBankPopup
        open={showGeneratePopup}
        handleClose={() => setShowGeneratePopup(false)}
        context="topic"
        contextKey={topicKey}
        folderId={activeFolder ? activeFolder.id : null}
        onSave={(payload) => {
          setShowGeneratePopup(false);
          if (activeFolder) {
            handleFolderClick(activeFolder);
          } else {
            fetchData();
          }
        }}
      />}

      {isViewPopupOpen&&<QuestionBankViewPopup
        open={isViewPopupOpen}
        questionBankId={viewBankId}
        context="topic"
        contextKey={topicKey}
        isAdminBank={activeFolder?.is_admin || false}
        onClose={() => {
          setIsViewPopupOpen(false);
          setViewBankId(null);
        }}
      />}

     {showCreateFolderPopup&& <CreateFolderPopup
        open={showCreateFolderPopup}
        handleClose={() => setShowCreateFolderPopup(false)}
        title="topic"
        contextKey={topicKey}
        mode="create"
        onSave={async (payload) => {
          try {
            setLoading(true);
            await createTopicFolder(payload.title, payload.key, payload.name);
            await fetchData();
          } catch (err) {
            console.error("Error creating topic folder:", err);
            setError("Failed to create folder");
          } finally {
            setLoading(false);
          }
        }}
      />}

    {!!editFolder &&  <CreateFolderPopup
        open={!!editFolder}
        handleClose={() => setEditFolder(null)}
        title="topic"
        contextKey={topicKey}
        mode="edit"
        initialName={editFolder?.name || ""}
        folderId={editFolder?.id}
        onSave={async (payload) => {
          try {
            if (!payload.id) return;
            setLoading(true);
            if (isInstitution) {
              await updateInstitutionQuestionBankFolder({
                id: payload.id,
                name: payload.name,
              });
            } else {
              await updateFolder(payload.id, payload.name);
            }
            await fetchData();
          } catch (err) {
            console.error("Error updating topic folder:", err);
            setError("Failed to update folder");
          } finally {
            setLoading(false);
            setEditFolder(null);
          }
        }}
      />}

      {isConfirmDeleteOpen&& <DeleteConfirmPopUp
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        title="Delete folder"
        message={`Are you sure you want to delete the folder "${
          folderToDelete?.name || ""
        }"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        handleDelete={async () => {
          if (!folderToDelete?.id) {
            setIsConfirmDeleteOpen(false);
            return;
          }
          try {
            setLoading(true);
            if (isInstitution) {
              await deleteInstitutionQuestionBankFolder(folderToDelete.id);
            } else {
              await deleteFolder(folderToDelete.id);
            }
            await fetchData();
          } catch (err) {
            console.error("Error deleting topic folder:", err);
            setError("Failed to delete folder");
          } finally {
            setLoading(false);
            setIsConfirmDeleteOpen(false);
            setFolderToDelete(null);
          }
        }}
      />}
    </>
  );
};

export default QuestionBankSubtopics;
